import '@babel/polyfill';
import React, {Fragment} from 'react'
import Router from 'next/router';
import Block from '../adapters/helpers/Block';
import PropTypes from 'prop-types'
import contentfulClient from '../adapters/model/ContentfulClient';
import Head from 'next/head';
import Meta from '../components/Meta/Meta';
import GTMScriptFunction from '../components/GTMScriptFunction';
import ImageFactory from '../adapters/cloudinary/ImageFactory';
import { ImageConstants } from '../adapters/helpers/Constants';

class Page extends React.Component {

  componentDidMount() {
    Router.beforePopState(({as}) => {
      location.href = as;
    });
  }

  static async getInitialProps({ asPath, query }) {
    const slug = query?.slug || asPath;
    const siteLocale = process.env.SITE_LOCALE;
    let languageCode, countryCode;
    if (siteLocale) {
      languageCode = siteLocale.substring(0, 2);
      countryCode = siteLocale.slice(-2);
    } else {
      const client = await contentfulClient.getClient();
      const locales = await client.getLocales();
      const defaultLocale = locales.items.find(locale => locale.default);
      languageCode = defaultLocale.code.substring(0, 2);
      countryCode = defaultLocale.code.slice(-2);
    }

    const baseProps = await this.initPage(slug);
    baseProps.languageCode = languageCode;
    baseProps.countryCode = countryCode;

    return {
      ...baseProps
    }
  }

  static async initPage(slug) {
    let page = {};

    // Errors pages doesn't have a page entry in Contentful CMS
    if (slug) {
      const { default: pageDao } = await import('Model/dao/PageDao');
      page = await pageDao.getActivePageBySlug(slug);
    }

    page?.fields?.blocks[0]?.fields?.blocks[1]?.fields?.blocks?.map((blocksArr, index)=>{
      // Mark the first block's images as LCP candidates
      const isFirstBlock = index === 0;
      
      if(blocksArr?.fields?.devicesConfigurations && Array.isArray(blocksArr.fields.devicesConfigurations)){
        blocksArr.fields.devicesConfigurations.map((devices)=>{
            if(devices?.fields?.backgroundAsset && devices?.fields?.backgroundAsset?.fields){
                devices.fields.backgroundAsset.fields.isBannerImage = true;
                devices.fields.backgroundAsset.fields.isPreload = true;
                if (isFirstBlock) {
                  devices.fields.backgroundAsset.fields.isLCP = true;
                }
            }
        })
     }
     if(blocksArr?.fields?.backgroundAsset && blocksArr?.fields?.backgroundAsset?.fields){
            blocksArr.fields.backgroundAsset.fields.isBannerImage = true;
            blocksArr.fields.backgroundAsset.fields.isPreload = true;
            if (isFirstBlock) {
              blocksArr.fields.backgroundAsset.fields.isLCP = true;
            }
        }
      if(blocksArr?.fields?.mobileBackgroundAsset && blocksArr?.fields?.mobileBackgroundAsset?.fields){
          blocksArr.fields.mobileBackgroundAsset.fields.isBannerImage = true;
          blocksArr.fields.mobileBackgroundAsset.fields.isPreload = true;
          if (isFirstBlock) {
            blocksArr.fields.mobileBackgroundAsset.fields.isLCP = true;
          }
      }
      if(blocksArr?.fields?.mobileBackgroundImage && blocksArr?.fields?.mobileBackgroundImage?.fields){
        blocksArr.fields.mobileBackgroundImage.fields.isBannerImage = true;
        blocksArr.fields.mobileBackgroundImage.fields.isPreload = true;
        if (isFirstBlock) {
          blocksArr.fields.mobileBackgroundImage.fields.isLCP = true;
        }
      }
      
      // Also check for main images and side images that could be LCP
      if (isFirstBlock) {
        if(blocksArr?.fields?.mainAsset && blocksArr?.fields?.mainAsset?.fields){
          blocksArr.fields.mainAsset.fields.isBannerImage = true;
          blocksArr.fields.mainAsset.fields.isPreload = true;
          blocksArr.fields.mainAsset.fields.isLCP = true;
        }
        if(blocksArr?.fields?.sideImage && blocksArr?.fields?.sideImage?.fields){
          blocksArr.fields.sideImage.fields.isBannerImage = true;
          blocksArr.fields.sideImage.fields.isPreload = true;
          blocksArr.fields.sideImage.fields.isLCP = true;
        }
      }
    })

    return {
      page,
      lcpImagePreloads: this.getLcpImagePreloads(page)
    };
  }

  static getLcpImagePreloads(page) {
    const firstContentBlock = page?.fields?.blocks?.[0]?.fields?.blocks?.[1]?.fields?.blocks?.[0]?.fields;
    if (!firstContentBlock) {
      return {};
    }

    const deviceConfigurations = Array.isArray(firstContentBlock.devicesConfigurations) ? firstContentBlock.devicesConfigurations : [];
    const desktopConfiguration = deviceConfigurations.find(configuration => configuration?.fields?.deviceType === 'desktop');
    const mobileConfiguration = deviceConfigurations.find(configuration => configuration?.fields?.deviceType === 'tiny') || deviceConfigurations.find(configuration => configuration?.fields?.deviceType === 'mobile');

    const desktopPath =
      desktopConfiguration?.fields?.backgroundAsset?.fields?.asset?.fields?.file?.url ||
      firstContentBlock.backgroundAsset?.fields?.asset?.fields?.file?.url;

    const mobilePath =
      mobileConfiguration?.fields?.backgroundAsset?.fields?.asset?.fields?.file?.url ||
      firstContentBlock.mobileBackgroundAsset?.fields?.asset?.fields?.file?.url ||
      firstContentBlock.mobileBackgroundImage?.fields?.asset?.fields?.file?.url ||
      desktopPath;

    return {
      mobile: mobilePath ? ImageFactory.buildContentfullImageUrlByWidth(mobilePath, ImageConstants.defaultDesktopWidth) : null,
      desktop: desktopPath ? ImageFactory.buildContentfullImageUrlByWidth(desktopPath, ImageConstants.lcpDesktopWidth) : null
    };
  }

  constructor(props) {
    super(props);
  }

  /**
   * Rendering of the page. This always render the page using a common layout.
   *
   * Override the render method to do otherwise.
   *
   * @returns {*}
   */
  render() {
    let page = this.getPage();
    if (!page) {
      return <div className="ob-wrapper" />;
    }
    const pagePath = page?.slug?.fields?.slug;
    const lcpImagePreloads = this.props?.lcpImagePreloads || {};
    let metaData = this.getMetadata();
    if(metaData){
      metaData.pageType = 'page'
    }
    return (
      <Fragment>
        <Head>
          {lcpImagePreloads.mobile && <link rel="preload" href={lcpImagePreloads.mobile} as="image" media={`(max-width:${ImageConstants.mobile_max_width})`} fetchPriority="high" />}
          {lcpImagePreloads.desktop && <link rel="preload" href={lcpImagePreloads.desktop} as="image" media={`(min-width:${ImageConstants.desktop_min_width})`} fetchPriority="high" />}
        </Head>
        <Meta metadata={metaData} pagePath={pagePath} />
        <div className="ob-wrapper">
          {this.renderBody()}
        </div>
        <GTMScriptFunction languageCode={this.props.languageCode} countryCode={this.props.countryCode} />
      </Fragment>
    )
  }

  getPage() {
    return this.props?.page?.fields;
  }

  getMetadata() {
    return this.props?.page?.fields?.metadata?.fields;
  }

  /**
   * By default, the page will render the modules of the page.
   * Can be overridden on the child class to do specific rendering.
   * @returns {*|void}
   */
  renderBody() {
    if (!this.props?.page) {
      return null;
    }
    return new Block(this.props.page).renderChildBlocks();
  }
}

Page.propTypes = {
  page: PropTypes.object,
  lcpImagePreloads: PropTypes.shape({
    mobile: PropTypes.string,
    desktop: PropTypes.string
  }),
  languageCode: PropTypes.string,
  countryCode: PropTypes.string
}

export default Page
